import React, { useEffect, useMemo, useState } from 'react';
import { flatten } from 'flat';
import { ChevronUp, ChevronDown, Pencil, Trash2, Eye, MoreVertical } from 'lucide-react';
import Dropdown, { DropdownItem } from './Dropdown';
import Spinner from './Spinner';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

// Drop-in replacement for the old MUI CustomizableTable — same prop contract
// (title, headCells[{id,label,render?}], rows, moreOptions[{title,handler}], onEdit, onDelete, onView)
// restyled to match the SEHA mockup table look.
export default function DataTable({
  title,
  headCells = [],
  rows = [],
  moreOptions = [],
  onEdit,
  onDelete,
  onView,
  pageSize = 10,
  emptyLabel = 'No records found.',
  loading = false,
}) {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = pageSize;

  useEffect(() => setPage(0), [rows]);

  const sortedRows = useMemo(() => {
    if (!orderBy) return rows;
    const withFlat = rows.map((row) => ({ row, flat: flatten(row) }));
    const sorted = stableSort(withFlat, (a, b) => getComparator(order, orderBy)(a.flat, b.flat));
    return sorted.map((entry) => entry.row);
  }, [rows, order, orderBy]);

  const visibleRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));

  const handleSort = (id) => {
    if (orderBy === id) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(id);
      setOrder('asc');
    }
  };

  const hasActions = Boolean(onEdit || onDelete || onView || moreOptions.length > 0);

  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle overflow-hidden">
      {title && (
        <div className="px-6 py-4 border-b border-neutral-100">
          <h3 className="font-bold text-neutral-800">{title}</h3>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="bg-neutral-50/70 text-neutral-500 text-xs uppercase tracking-wider font-semibold border-b border-neutral-200">
            <tr>
              {headCells.map((cell) => (
                <th key={cell.id} className="px-6 py-4 select-none">
                  <button
                    className="inline-flex items-center gap-1 hover:text-neutral-800"
                    onClick={() => handleSort(cell.id)}
                  >
                    {cell.label}
                    {orderBy === cell.id &&
                      (order === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </button>
                </th>
              ))}
              {hasActions && <th className="px-6 py-4 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 text-sm">
            {visibleRows.length === 0 && (
              <tr>
                <td className="px-6 py-10 text-center text-neutral-400" colSpan={headCells.length + (hasActions ? 1 : 0)}>
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <Spinner size="sm" />
                      <span>{emptyLabel}</span>
                    </div>
                  ) : (
                    emptyLabel
                  )}
                </td>
              </tr>
            )}
            {visibleRows.map((row, idx) => {
              const flatRow = flatten(row);
              return (
                <tr key={row.id ?? idx} className="hover:bg-neutral-50/80 transition-colors">
                  {headCells.map((cell) => (
                    <td key={cell.id} className="px-6 py-4 text-neutral-700">
                      {cell.render ? cell.render(row) : flatRow[cell.id]}
                    </td>
                  ))}
                  {hasActions && (
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {onEdit && (
                          <button onClick={() => onEdit(row)} className="p-1.5 rounded-lg hover:bg-neutral-100 text-seha-500" title="Edit">
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {onDelete && (
                          <button onClick={() => onDelete(row)} className="p-1.5 rounded-lg hover:bg-neutral-100 text-orange-600" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        {onView && (
                          <button onClick={() => onView(row)} className="p-1.5 rounded-lg hover:bg-neutral-100 text-green-600" title="View">
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {moreOptions.length > 0 && (
                          <Dropdown
                            trigger={({ toggle }) => (
                              <button onClick={toggle} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-500" title="More">
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            )}
                          >
                            {({ close }) =>
                              moreOptions.map((opt) => (
                                <DropdownItem
                                  key={opt.title}
                                  onClick={() => {
                                    opt.handler(row);
                                    close();
                                  }}
                                >
                                  {opt.title}
                                </DropdownItem>
                              ))
                            }
                          </Dropdown>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > rowsPerPage && (
        <div className="flex items-center justify-between px-6 py-3 border-t border-neutral-100 text-xs text-neutral-500">
          <span>
            Page {page + 1} of {totalPages} &middot; {rows.length} rows
          </span>
          <div className="flex gap-2">
            <button
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="px-3 py-1.5 rounded-lg border border-neutral-200 disabled:opacity-40 hover:bg-neutral-50"
            >
              Prev
            </button>
            <button
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="px-3 py-1.5 rounded-lg border border-neutral-200 disabled:opacity-40 hover:bg-neutral-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
